/* iFitCore Muscle Finder - UI logic (vanilla JS, no dependencies).
   All customer-facing text comes from Shopify locale files via the section's JSON block (data-ifc-mf-i18n).
   Internal ids (muscle ids, product tags, filter ids, GLB names) are language independent. */
(function () {
  'use strict';
  if (window.IFCMuscleFinder) { window.IFCMuscleFinder.scan(); return; }

  /* muscle id -> accepted product tags (technical, never translated) */
  var MUSCLE_TAGS = {
    chest: ['muscle:chest'],
    shoulders: ['muscle:shoulders'],
    biceps: ['muscle:biceps'],
    triceps: ['muscle:triceps'],
    forearms: ['muscle:forearms'],
    back: ['muscle:back', 'muscle:lower-back'],
    core: ['muscle:core'],
    glutes: ['muscle:glutes'],
    legs: ['muscle:legs', 'muscle:quads', 'muscle:hamstrings'],
    calves: ['muscle:calves']
  };
  var CATEGORIES = ['all', 'machine', 'bench', 'weights', 'other'];
  /* fallback when no finder:* tag exists: Product Type may be in any storefront language */
  var TYPE_RULES = [
    ['machine', /(machine|maschine|ger[aä]t|gép|station|rack|cable|kabel|smith|erőkeret|keret|multi)/],
    ['bench', /(bench|bank|b[aä]nk|pad\b|padok)/],
    ['weights', /(dumbbell|barbell|weight|gewicht|hantel|kettlebell|plate|scheibe|súly|tárcsa|rúd)/]
  ];

  /* ---- Rehabilitation mode config. Technical ids only (never translated); copy lives in the Shopify locale files. ----
     Product tags: rehab:<region>  goal:<goal>  experience:<id>  activity:<id>   (underscores in ids become hyphens in tags) */
  function tagOf(prefix, id) { return prefix + ':' + String(id).replace(/_/g, '-'); }
  var REHAB = {
    regions: ['neck', 'shoulder', 'upper_back', 'lower_back', 'elbow', 'wrist_hand', 'hip', 'knee', 'ankle', 'foot'],
    experiences: ['stiffness', 'load_discomfort', 'post_training_sensitivity', 'muscle_tension', 'instability', 'recovery'],
    goals: ['mobility', 'stability', 'gentle_strength', 'recovery'],
    flags: ['trauma', 'severe_pain', 'cannot_bear_weight', 'swelling', 'numbness', 'weakness'],
    /* region-specific "when / during what" answer sets */
    activities: {
      neck: ['desk_work', 'turning_head', 'overhead', 'after_training', 'general'],
      shoulder: ['overhead', 'pressing', 'pulling', 'resting_position', 'after_training', 'general'],
      upper_back: ['desk_work', 'pulling', 'overhead', 'after_training', 'general'],
      lower_back: ['sitting', 'bending', 'lifting', 'standing', 'after_training', 'general'],
      elbow: ['gripping', 'pressing', 'pulling', 'after_training', 'general'],
      wrist_hand: ['gripping', 'typing', 'pressing', 'after_training', 'general'],
      hip: ['sitting', 'squat', 'walking', 'after_training', 'general'],
      knee: ['squat', 'stairs', 'running', 'after_sitting', 'after_training', 'general'],
      ankle: ['walking', 'running', 'jumping', 'after_training', 'general'],
      foot: ['walking', 'running', 'standing', 'after_training', 'general']
    },
    /* transparent scoring: region tag is REQUIRED, the rest only ranks */
    weights: { goal: 3, experience: 2, activity: 1 },
    extraRegionTags: { wrist_hand: ['rehab:wrist', 'rehab:hand'] }
  };
  var REGION_TAGS = {};
  REHAB.regions.forEach(function (id) { REGION_TAGS[id] = [tagOf('rehab', id)].concat(REHAB.extraRegionTags[id] || []); });
  var REHAB_FILTERS = ['all', 'mobility', 'stability', 'recovery', 'gentle_strength'];

  /* keep the active chip visible inside a horizontally scrolling chip strip (mobile) - never scrolls the page itself */
  function revealInNav(nav, b) {
    if (!nav || !b || nav.scrollWidth <= nav.clientWidth + 2) return;
    var nr = nav.getBoundingClientRect(), br = b.getBoundingClientRect();
    var left = nav.scrollLeft + (br.left - nr.left) - (nr.width - br.width) / 2;
    if (nav.scrollTo) nav.scrollTo({ left: left, behavior: reduced ? 'auto' : 'smooth' }); else nav.scrollLeft = left;
  }

  var instances = new Map();
  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function Finder(root) {
    this.root = root;
    this.ac = new AbortController();
    this.viewer = null;
    this.viewerLoading = false;
    this.io = null;
    this.timers = [];
    this.inView = true;
    this.destroyed = false;
    this.category = 'all';
    this.muscle = 'chest';
    this.mode = 'training';
    /* independent in-memory selection state per mode */
    this.rehab = { region: 'knee', experience: null, activity: null, goal: null, flags: {}, category: 'all' };
    this.i18n = this.readI18n();
    this.collect();
    this.init();
  }

  Finder.prototype.readI18n = function () {
    var node = this.root.querySelector('script[data-ifc-mf-i18n]');
    try { return JSON.parse(node ? node.textContent : '{}'); } catch (e) { return {}; }
  };

  Finder.prototype.readSources = function () {
    var node = this.root.querySelector('script[data-ifc-mf-sources]');
    try {
      var v = JSON.parse(node ? node.textContent : '{}');
      return { training: Array.isArray(v.training) ? v.training : [], rehab: Array.isArray(v.rehab) ? v.rehab : [] };
    } catch (e) { return { training: [], rehab: [] }; }
  };

  Finder.prototype.collect = function () {
    var r = this.root;
    this.navButtons = [].slice.call(r.querySelectorAll('[data-ifc-muscle]'));
    this.filterButtons = [].slice.call(r.querySelectorAll('[data-ifc-filter]'));
    this.cards = [].slice.call(r.querySelectorAll('[data-ifc-card]'));
    this.els = {
      title: r.querySelector('[data-ifc-title]'), latin: r.querySelector('[data-ifc-latin]'), desc: r.querySelector('[data-ifc-desc]'),
      count: r.querySelector('[data-ifc-count]'), countLabel: r.querySelector('[data-ifc-count-label]'),
      empty: r.querySelector('[data-ifc-empty]'), tagHint: r.querySelector('[data-ifc-tag-hint]'),
      viewer: r.querySelector('[data-ifc-viewer]'), canvas: r.querySelector('[data-ifc-canvas]'), status: r.querySelector('[data-ifc-status]'),
      hover: r.querySelector('[data-ifc-hover]'), reset: r.querySelector('[data-ifc-reset]'), live: r.querySelector('[data-ifc-live]')
    };
    this.regionButtons = [].slice.call(r.querySelectorAll('[data-ifc-region]'));
    this.rfilterButtons = [].slice.call(r.querySelectorAll('[data-ifc-rfilter]'));
    this.modeButtons = [].slice.call(r.querySelectorAll('[data-ifc-mode]'));
    this.rehabOn = r.dataset.rehab === 'true' && this.regionButtons.length > 0;
    this.els.eyebrow = r.querySelector('[data-ifc-eyebrow]'); this.els.summary = r.querySelector('[data-ifc-summary]');
    this.els.main = r.querySelector('[data-ifc-main]'); this.els.products = r.querySelector('[data-ifc-products]');
    this.els.emptyText = r.querySelector('[data-ifc-empty-text]'); this.els.countBox = r.querySelector('.ifc-mf__count');
    this.els.assess = r.querySelector('[data-ifc-assess]'); this.els.steps = r.querySelector('[data-ifc-steps]');
    this.els.redflag = r.querySelector('[data-ifc-redflag]'); this.els.redflagText = r.querySelector('[data-ifc-redflag-text]');
    this.els.flagsReset = r.querySelector('[data-ifc-flags-reset]'); this.els.safety = r.querySelector('[data-ifc-safety]');
    this.els.flags = r.querySelector('[data-ifc-flags]'); this.els.experience = r.querySelector('[data-ifc-experience]');
    this.els.activity = r.querySelector('[data-ifc-activity]'); this.els.goal = r.querySelector('[data-ifc-goal]');
    this.els.disclaimer = r.querySelector('[data-ifc-disclaimer]'); this.els.tfilters = r.querySelector('[data-ifc-tfilters]');
    this.els.rfilters = r.querySelector('[data-ifc-rfilters]'); this.els.rehabNav = r.querySelector('[data-ifc-rehab-nav]');
    this.els.nav = r.querySelector('[data-ifc-nav]');
    this.cards.forEach(function (c, i) { c._i = i; });
    this.limit = Math.max(1, parseInt(r.dataset.productsLimit, 10) || 6);
    this.hideSoldOut = r.dataset.hideSoldOut === 'true';
  };

  Finder.prototype.on = function (target, type, fn, opts) {
    if (!target) return;
    target.addEventListener(type, fn, Object.assign({ signal: this.ac.signal }, opts || {}));
  };

  Finder.prototype.init = function () {
    var self = this, initial = this.root.dataset.initialMuscle;
    this.muscle = MUSCLE_TAGS[initial] ? initial : 'chest';

    this.navButtons.forEach(function (b) { self.on(b, 'click', function () { self.select(b.dataset.ifcMuscle, { announce: true }); }); });
    this.filterButtons.forEach(function (b) {
      self.on(b, 'click', function () {
        self.category = b.dataset.ifcFilter;
        self.filterButtons.forEach(function (x) { var on = x === b; x.classList.toggle('is-active', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        self.renderProducts();
      });
    });
    this.cards.forEach(function (card) { self.wireCard(card); });
    this.on(this.els.reset, 'click', function () { if (self.viewer) self.viewer.resetView(); });
    this.on(this.els.products, 'click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]'), card = a && a.closest('[data-ifc-card]');
      if (card) self.track('finder_product_clicked', { productId: card.dataset.productId, mode: self.mode });
    });

    this.select(this.muscle, { silent: true });
    if (this.rehabOn) this.initRehab();
    this.loadRemoteProducts();
    this.measureFrame();
    var queued = false;
    var again = function () { if (queued) return; queued = true; requestAnimationFrame(function () { queued = false; if (!self.destroyed) self.measureFrame(); }); };
    this.on(window, 'resize', again);
    this.on(window, 'orientationchange', again);
    this.on(window, 'load', again);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(again);
    this.observeViewer();
    this.on(document, 'visibilitychange', function () { self.syncActive(); });
  };

  /* Desktop: the whole finder is one fixed-height dashboard that fits the viewport below the theme header.
     The height is measured here (header height differs per theme); mobile uses natural document flow. */
  Finder.prototype.measureFrame = function () {
    var frame = this.root.querySelector('.ifc-mf__frame');
    if (!frame) return;
    if (!window.matchMedia || !window.matchMedia('(min-width: 901px)').matches) { frame.style.removeProperty('--ifc-frame-h'); return; }
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var top = frame.getBoundingClientRect().top;
    var hdr = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    var offset = top > hdr + 1 ? top : hdr;      /* below the header already (page top) or flush under a sticky header */
    var h = Math.max(600, Math.min(1000, Math.round(vh - offset)));
    frame.style.setProperty('--ifc-frame-h', h + 'px');
  };

  Finder.prototype.wireCard = function (card) {
    if (!card || card.__ifcWired) return;
    card.__ifcWired = true;
    var self = this, btn = card.querySelector('[data-ifc-add]');
    if (btn) {
      var label = btn.querySelector('[data-ifc-add-label]');
      btn.dataset.label = label ? label.textContent : '';
      this.on(btn, 'click', function () { self.addToCart(btn); });
    }
  };

  Finder.prototype.money = function (money) {
    if (!money || money.amount == null) return '';
    var amount = Number(money.amount);
    if (!isFinite(amount)) return '';
    var currency = money.currency || 'HUF';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'hu-HU', {
        style: 'currency', currency: currency,
        maximumFractionDigits: currency === 'HUF' ? 0 : 2
      }).format(amount);
    } catch (e) { return amount + ' ' + currency; }
  };

  Finder.prototype.buildRemoteCard = function (p) {
    var self = this, a = this.i18n.actions || {}, badges = this.i18n.badges || {};
    var card = document.createElement('article');
    card.className = 'ifc-mf__card';
    card.setAttribute('data-ifc-card', '');
    card.dataset.modes = p.modes || '';
    card.dataset.tags = (p.tags || []).join('|');
    card.dataset.productType = String(p.productType || '').toLowerCase();
    card.dataset.available = p.available ? 'true' : 'false';
    card.dataset.productId = String(p.id || '');
    card.hidden = true;

    var imageLink = document.createElement('a');
    imageLink.className = 'ifc-mf__card-image';
    imageLink.href = p.url || ('/products/' + p.handle);
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');
    if (p.image && p.image.url) {
      var img = document.createElement('img');
      img.src = p.image.url;
      img.alt = p.image.alt || p.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      imageLink.appendChild(img);
    }
    card.appendChild(imageLink);

    var sale = p.compareAtPrice != null && p.price && Number(p.compareAtPrice) > Number(p.price.amount);
    if (sale || p.popular) {
      var badge = document.createElement('span');
      badge.className = 'ifc-mf__badge';
      badge.textContent = sale ? (badges.sale || '') : (badges.popular || '');
      if (badge.textContent) card.appendChild(badge);
    }

    var body = document.createElement('div');
    body.className = 'ifc-mf__card-body';

    var title = document.createElement('a');
    title.className = 'ifc-mf__product-title';
    title.href = p.url || ('/products/' + p.handle);
    title.textContent = p.title || '';
    body.appendChild(title);

    var price = document.createElement('div');
    price.className = 'ifc-mf__price';
    var now = document.createElement('span');
    now.textContent = this.money(p.price);
    price.appendChild(now);
    if (sale) {
      var old = document.createElement('s');
      old.textContent = this.money({ amount: p.compareAtPrice, currency: p.price.currency });
      price.appendChild(old);
    }
    body.appendChild(price);

    if (!p.available) {
      var sold = document.createElement('button');
      sold.type = 'button'; sold.className = 'ifc-mf__cart'; sold.disabled = true;
      sold.textContent = a.soldOut || '';
      body.appendChild(sold);
    } else if (this.root.dataset.ajaxCart === 'true' && p.hasOnlyDefaultVariant && p.variantId) {
      var btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'ifc-mf__cart';
      btn.setAttribute('data-ifc-add', '');
      btn.dataset.variantId = String(p.variantId);
      btn.dataset.productId = String(p.id || '');
      btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2.500l2 11h10l2-8H7M9.500 20h.01M16.500 20h.01"/></svg>';
      var lab = document.createElement('span');
      lab.setAttribute('data-ifc-add-label', '');
      lab.textContent = a.addToCart || '';
      btn.appendChild(lab);
      body.appendChild(btn);
    } else {
      var choose = document.createElement('a');
      choose.className = 'ifc-mf__cart';
      choose.href = p.url || ('/products/' + p.handle);
      choose.textContent = a.chooseOptions || '';
      body.appendChild(choose);
    }

    card.appendChild(body);
    self.wireCard(card);
    return card;
  };

  Finder.prototype.loadRemoteProducts = function () {
    var self = this, api = this.root.dataset.finderApi;
    if (!api || !this.els.products || this.remoteLoading) return;
    var sources = this.readSources();
    var training = (sources.training || []).filter(Boolean);
    var rehab = (sources.rehab || []).filter(Boolean);
    if (!training.length && !rehab.length) return;

    this.remoteLoading = true;
    var qs = new URLSearchParams();
    if (training.length) qs.set('training', training.join(','));
    if (rehab.length) qs.set('rehab', rehab.join(','));

    fetch(api + '?' + qs.toString(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('finder api ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data || data.ok !== true || !Array.isArray(data.products) || self.destroyed) return;

        var existing = new Map();
        self.cards.forEach(function (card) { if (card.dataset.productId) existing.set(String(card.dataset.productId), card); });
        var keep = new Set();

        data.products.forEach(function (p) {
          var id = String(p.id || '');
          if (!id) return;
          keep.add(id);
          var card = existing.get(id);
          if (card) {
            card.dataset.modes = p.modes || '';
            card.dataset.tags = (p.tags || []).join('|');
            card.dataset.productType = String(p.productType || '').toLowerCase();
            card.dataset.available = p.available ? 'true' : 'false';
          } else {
            card = self.buildRemoteCard(p);
            self.els.products.appendChild(card);
            existing.set(id, card);
          }
        });

        // Once the Admin API succeeded it is the canonical source. Remove stale Liquid-only cards.
        self.cards.forEach(function (card) {
          var id = String(card.dataset.productId || '');
          if (id && !keep.has(id)) card.remove();
        });

        self.cards = [].slice.call(self.root.querySelectorAll('[data-ifc-card]'));
        self.cards.forEach(function (card, i) { card._i = i; self.wireCard(card); });
        self.root.dataset.remoteProducts = 'ready';
        self.renderProducts();
      })
      .catch(function (err) {
        self.root.dataset.remoteProducts = 'fallback';
        if (window.console) console.warn('[iFitCore Muscle Finder] product API unavailable; Liquid fallback remains active:', err);
      })
      .then(function () { self.remoteLoading = false; });
  };

  /* ---------------- products ---------------- */
  function tagsOf(card) { return (card.dataset.tags || '').split('|').map(function (t) { return t.trim(); }).filter(Boolean); }
  Finder.prototype.hasMuscle = function (card, muscle) {
    var tags = tagsOf(card), accepted = MUSCLE_TAGS[muscle] || [];
    return accepted.some(function (t) { return tags.indexOf(t) >= 0; });
  };
  Finder.prototype.categoryOf = function (card) {
    var tags = tagsOf(card);
    for (var i = 1; i < CATEGORIES.length; i++) if (tags.indexOf('finder:' + CATEGORIES[i]) >= 0) return CATEGORIES[i];
    var type = (card.dataset.productType || '').toLowerCase();
    for (var j = 0; j < TYPE_RULES.length; j++) if (TYPE_RULES[j][1].test(type)) return TYPE_RULES[j][0];
    return 'other';
  };
  Finder.prototype.inMode = function (card, mode) {
    var m = card.dataset.modes;
    return m ? (' ' + m + ' ').indexOf(' ' + mode + ' ') >= 0 : mode === 'training';
  };
  Finder.prototype.available = function (card) { return !this.hideSoldOut || card.dataset.available === 'true'; };
  Finder.prototype.eligible = function (card) {
    return this.inMode(card, 'training') && this.hasMuscle(card, this.muscle) && this.available(card);
  };
  Finder.prototype.eligibleRehab = function (card) {
    return this.inMode(card, 'rehab') && this.hasRegion(card, this.rehab.region) && this.available(card);
  };
  Finder.prototype.hasRegion = function (card, region) {
    var tags = tagsOf(card);
    return (REGION_TAGS[region] || []).some(function (t) { return tags.indexOf(t) >= 0; });
  };
  /* rehab ranking: region required; goal +3, experience +2, activity +1 (all optional, only reorder) */
  Finder.prototype.rehabScore = function (card) {
    var tags = tagsOf(card), r = this.rehab, w = REHAB.weights, score = 0;
    if (r.goal && tags.indexOf(tagOf('goal', r.goal)) >= 0) score += w.goal;
    if (r.experience && tags.indexOf(tagOf('experience', r.experience)) >= 0) score += w.experience;
    if (r.activity && tags.indexOf(tagOf('activity', r.activity)) >= 0) score += w.activity;
    return score;
  };

  /* show the first `limit` of `list` (already sorted), hide the rest, restore a stable DOM order */
  Finder.prototype.applyCards = function (list) {
    var self = this, shown = 0, container = this.els.products, inList = new Set(list);
    list.forEach(function (card) { if (container && container.lastElementChild !== card) container.appendChild(card); });
    this.cards.forEach(function (card) {
      var show = inList.has(card) && shown < self.limit;
      card.hidden = !show;
      if (show) shown++;
    });
    return shown;
  };

  Finder.prototype.renderProducts = function () {
    if (this.mode === 'rehab') return this.renderRehabProducts();
    var self = this, counts = { all: 0, machine: 0, bench: 0, weights: 0, other: 0 }, list = [];
    this.cards.forEach(function (card) {
      if (!self.eligible(card)) return;
      counts.all++; counts[self.categoryOf(card)]++;
      if (self.category === 'all' || self.categoryOf(card) === self.category) list.push(card);
    });
    list.sort(function (a, b) { return a._i - b._i; });
    var shown = this.applyCards(list);
    CATEGORIES.forEach(function (c) {
      var el = self.root.querySelector('[data-ifc-filter-count="' + c + '"]');
      if (el) el.textContent = '(' + counts[c] + ')';
    });
    if (this.els.count) this.els.count.textContent = String(counts.all);
    if (this.els.countLabel && this.i18n.count) this.els.countLabel.textContent = counts.all === 1 ? this.i18n.count.one : this.i18n.count.other;
    if (this.els.empty) {
      this.els.empty.hidden = shown > 0;
      if (this.els.emptyText && this.i18n.empty) this.els.emptyText.textContent = this.i18n.empty.noProducts;
      if (this.els.tagHint && this.i18n.empty) this.els.tagHint.textContent = this.i18n.empty.tagHint.replace('__TAG__', MUSCLE_TAGS[this.muscle][0]);
    }
  };

  Finder.prototype.renderRehabProducts = function () {
    var self = this, r = this.rehab, rl = this.i18n.rehab || {}, counts = { all: 0 }, list = [];
    REHAB.goals.forEach(function (g) { counts[g] = 0; });
    this.cards.forEach(function (card) {
      if (!self.eligibleRehab(card)) return;
      var tags = tagsOf(card);
      counts.all++;
      REHAB.goals.forEach(function (g) { if (tags.indexOf(tagOf('goal', g)) >= 0) counts[g]++; });
      if (r.category === 'all' || tags.indexOf(tagOf('goal', r.category)) >= 0) { card._score = self.rehabScore(card); list.push(card); }
    });
    list.sort(function (a, b) { return (b._score - a._score) || (a._i - b._i); });
    var shown = this.applyCards(list);
    REHAB_FILTERS.forEach(function (c) {
      var el = self.root.querySelector('[data-ifc-rfilter-count="' + c + '"]');
      if (el) el.textContent = '(' + counts[c] + ')';
    });
    if (this.els.count) this.els.count.textContent = String(counts.all);
    if (this.els.countLabel && this.i18n.count) this.els.countLabel.textContent = counts.all === 1 ? this.i18n.count.one : this.i18n.count.other;
    if (this.els.empty) {
      this.els.empty.hidden = shown > 0 || this.flagged();
      if (this.els.emptyText && rl.empty) this.els.emptyText.textContent = rl.empty.noProducts;
      if (this.els.tagHint && rl.empty) {
        var hint = rl.empty.tagHint.replace('__TAG__', REGION_TAGS[r.region][0]);
        this.els.tagHint.textContent = (this.root.dataset.rehabSource !== 'true' ? rl.empty.setup + ' ' : '') + hint;
      }
    }
  };

  /* ---------------- selection ---------------- */
  Finder.prototype.select = function (id, o) {
    o = o || {};
    if (!MUSCLE_TAGS[id]) return;
    this.muscle = id;
    this.navButtons.forEach(function (b) {
      var on = b.dataset.ifcMuscle === id;
      b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on && o.fromViewer) revealInNav(b.parentNode, b);
    });
    var m = (this.i18n.muscles || {})[id];
    if (m && this.mode === 'training') {
      if (this.els.live && !o.silent && this.i18n.viewer) this.els.live.textContent = this.i18n.viewer.selected.replace('__MUSCLE__', m.name);
    }
    this.renderHeader();
    this.renderProducts();
    if (this.viewer) this.viewer.setSelected(id);
  };

  /* selected-area header: texts depend on the active mode */
  Finder.prototype.renderHeader = function () {
    var e = this.els;
    if (this.mode === 'rehab') {
      var rl = this.i18n.rehab || {}, reg = (rl.regions || {})[this.rehab.region];
      if (!reg) return;
      if (e.eyebrow) e.eyebrow.textContent = '\u203a ' + rl.selectedArea;
      if (e.title) e.title.textContent = reg.name;
      if (e.latin) e.latin.textContent = reg.focus;
      if (e.desc) e.desc.textContent = reg.description;
      if (e.summary) {
        var focus = (rl.focusGoal || {})[this.rehab.goal || 'all'] || '';
        e.summary.textContent = rl.summary.replace('__FOCUS__', focus).replace('__AROUND__', reg.around);
      }
      return;
    }
    var m = (this.i18n.muscles || {})[this.muscle];
    if (e.eyebrow && this.i18n.training) e.eyebrow.textContent = '\u203a ' + this.i18n.training.selectedGroup;
    if (m) {
      if (e.title) e.title.textContent = m.title;
      if (e.latin) e.latin.textContent = m.anatomy;
      if (e.desc) e.desc.textContent = m.description;
    }
  };

  Finder.prototype.hover = function (id, x, y, kind) {
    var h = this.els.hover, v = this.els.viewer;
    if (!h || !v) return;
    var m = id && (kind === 'region' ? ((this.i18n.rehab || {}).regions || {})[id] : (this.i18n.muscles || {})[id]);
    if (!m) { h.hidden = true; return; }
    var r = v.getBoundingClientRect();
    h.textContent = m.name;
    h.hidden = false;
    h.style.left = Math.min(Math.max(x - r.left, 0), r.width - 140) + 'px';
    h.style.top = Math.min(Math.max(y - r.top, 0), r.height - 40) + 'px';
  };

  /* ---------------- analytics (lightweight DOM events, no SDK) ---------------- */
  Finder.prototype.track = function (name, detail) {
    try {
      this.root.dispatchEvent(new CustomEvent('ifitcore:finder', { bubbles: true, detail: Object.assign({ event: name }, detail || {}) }));
    } catch (e) { /* old browsers */ }
  };

  /* ---------------- Rehabilitation mode ---------------- */
  Finder.prototype.flagged = function () {
    var f = this.rehab.flags;
    return REHAB.flags.some(function (id) { return !!f[id]; });
  };

  Finder.prototype.chip = function (id, label, pressed) {
    var b = document.createElement('button');
    b.type = 'button'; b.className = 'ifc-mf__chip' + (pressed ? ' is-active' : '');
    b.dataset.id = id; b.setAttribute('aria-pressed', pressed ? 'true' : 'false'); b.textContent = label;
    return b;
  };
  Finder.prototype.fillChips = function (box, ids, labels, isOn, onPick) {
    if (!box) return;
    var self = this;
    box.textContent = '';
    ids.forEach(function (id) {
      var b = self.chip(id, labels[id] || id, isOn(id));
      self.on(b, 'click', function () { onPick(id); });
      box.appendChild(b);
    });
  };

  Finder.prototype.initRehab = function () {
    var self = this, rl = this.i18n.rehab || {}, d = this.root.dataset;
    if (!rl.regions) { this.rehabOn = false; return; }
    this.rehab.region = REHAB.regions.indexOf(d.initialRegion) >= 0 ? d.initialRegion : 'knee';
    this.modeButtons.forEach(function (b) {
      self.on(b, 'click', function () { self.setMode(b.dataset.ifcMode, { announce: true }); });
      self.on(b, 'keydown', function (e) {
        var i = self.modeButtons.indexOf(b), n = self.modeButtons.length, to = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = (i + 1) % n;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = (i - 1 + n) % n;
        else if (e.key === 'Home') to = 0; else if (e.key === 'End') to = n - 1;
        if (to >= 0) { e.preventDefault(); self.modeButtons[to].focus(); self.setMode(self.modeButtons[to].dataset.ifcMode, { announce: true }); }
      });
    });
    this.regionButtons.forEach(function (b) { self.on(b, 'click', function () { self.selectRegion(b.dataset.ifcRegion, { announce: true }); }); });
    this.rfilterButtons.forEach(function (b) {
      self.on(b, 'click', function () {
        self.rehab.category = b.dataset.ifcRfilter;
        self.rfilterButtons.forEach(function (x) { var on = x === b; x.classList.toggle('is-active', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        self.renderProducts();
      });
    });
    this.on(this.els.flagsReset, 'click', function () {
      self.rehab.flags = {}; self.buildFlagChips(); self.renderRehabState();
      var first = self.els.flags && self.els.flags.querySelector('button'); if (first) first.focus();
    });
    /* static texts */
    [].slice.call(this.root.querySelectorAll('[data-ifc-step-title]')).forEach(function (n) {
      var t = (rl.steps || {})[n.dataset.ifcStepTitle];
      n.textContent = t || '';
      if (t && rl.steps.optional) { var o = document.createElement('small'); o.textContent = ' ' + rl.steps.optional; n.appendChild(o); }
    });
    if (this.els.flagsReset) this.els.flagsReset.textContent = (rl.safety || {}).change || '';
    if (this.els.redflagText) this.els.redflagText.textContent = (rl.safety || {}).message || '';
    this.buildFlagChips();
    this.buildExperienceChips();
    this.buildGoalChips();
    this.buildActivityChips();
    this.syncRegionNav();
    this.renderRehabState();
    if (d.initialMode === 'rehab') this.setMode('rehab', { silent: true });
  };

  Finder.prototype.buildFlagChips = function () {
    var self = this, rl = this.i18n.rehab || {};
    this.fillChips(this.els.flags, REHAB.flags, rl.flags || {}, function (id) { return !!self.rehab.flags[id]; }, function (id) {
      self.rehab.flags[id] = !self.rehab.flags[id];
      self.buildFlagChips(); self.renderRehabState();
      var again = self.els.flags && self.els.flags.querySelector('[data-id="' + id + '"]'); if (again) again.focus();
      self.track('rehab_safety_flag_toggled', { flag: id, on: !!self.rehab.flags[id], flagged: self.flagged() });
    });
  };
  Finder.prototype.buildExperienceChips = function () {
    var self = this, rl = this.i18n.rehab || {};
    this.fillChips(this.els.experience, REHAB.experiences, rl.experiences || {}, function (id) { return self.rehab.experience === id; }, function (id) {
      self.rehab.experience = self.rehab.experience === id ? null : id;
      self.buildExperienceChips(); self.afterAnswer();
      var again = self.els.experience.querySelector('[data-id="' + id + '"]'); if (again) again.focus();
      self.track('rehab_experience_selected', { experience: self.rehab.experience, region: self.rehab.region });
    });
  };
  Finder.prototype.buildGoalChips = function () {
    var self = this, rl = this.i18n.rehab || {};
    this.fillChips(this.els.goal, REHAB.goals, rl.goals || {}, function (id) { return self.rehab.goal === id; }, function (id) {
      self.rehab.goal = self.rehab.goal === id ? null : id;
      self.buildGoalChips(); self.afterAnswer();
      var again = self.els.goal.querySelector('[data-id="' + id + '"]'); if (again) again.focus();
      self.track('rehab_goal_selected', { goal: self.rehab.goal, region: self.rehab.region });
    });
  };
  Finder.prototype.buildActivityChips = function () {
    var self = this, rl = this.i18n.rehab || {}, ids = REHAB.activities[this.rehab.region] || [];
    this.fillChips(this.els.activity, ids, rl.activities || {}, function (id) { return self.rehab.activity === id; }, function (id) {
      self.rehab.activity = self.rehab.activity === id ? null : id;
      self.buildActivityChips(); self.afterAnswer();
      var again = self.els.activity.querySelector('[data-id="' + id + '"]'); if (again) again.focus();
      self.track('rehab_activity_selected', { activity: self.rehab.activity, region: self.rehab.region });
    });
  };
  Finder.prototype.afterAnswer = function () { this.renderHeader(); this.renderProducts(); };

  Finder.prototype.syncRegionNav = function () {
    var region = this.rehab.region;
    this.regionButtons.forEach(function (b) { var on = b.dataset.ifcRegion === region; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
  };

  /* red-flag state: calm message, no questionnaire, no products */
  Finder.prototype.renderRehabState = function () {
    var e = this.els, flagged = this.flagged(), rehab = this.mode === 'rehab';
    if (e.steps) e.steps.hidden = flagged;
    if (e.redflag) e.redflag.hidden = !flagged;
    if (e.rfilters) e.rfilters.hidden = flagged || !rehab;
    if (e.products) e.products.hidden = rehab && flagged;
    if (e.countBox) e.countBox.hidden = rehab && flagged;
    if (e.summary) e.summary.hidden = !rehab || flagged;
    if (rehab) this.renderProducts();
    if (e.empty && rehab && flagged) e.empty.hidden = true;
  };

  Finder.prototype.selectRegion = function (id, o) {
    o = o || {};
    if (REHAB.regions.indexOf(id) < 0) return;
    var changed = this.rehab.region !== id, rl = this.i18n.rehab || {};
    this.rehab.region = id;
    if (changed && this.rehab.activity && (REHAB.activities[id] || []).indexOf(this.rehab.activity) < 0) this.rehab.activity = null;   /* incompatible answer */
    this.syncRegionNav();
    if (o.fromViewer) { var cur = this.regionButtons.filter(function (b) { return b.dataset.ifcRegion === id; })[0]; revealInNav(cur && cur.parentNode, cur); }
    this.buildActivityChips();
    this.renderHeader();
    this.renderRehabState();
    if (this.viewer) this.viewer.setRegion(id);
    var reg = (rl.regions || {})[id];
    if (reg && this.els.live && !o.silent && rl.announce) this.els.live.textContent = rl.announce.replace('__REGION__', reg.name);
    if (changed || o.announce) this.track('rehab_region_selected', { region: id, source: o.fromViewer ? '3d' : 'ui' });
  };

  Finder.prototype.setMode = function (mode, o) {
    o = o || {};
    if (mode !== 'rehab' && mode !== 'training') return;
    if (mode === 'rehab' && !this.rehabOn) return;
    var changed = mode !== this.mode, rehab = mode === 'rehab', e = this.els, rl = this.i18n.rehab || {}, tr = this.i18n.training || {}, root = this.root;
    this.mode = mode;
    root.dataset.mode = mode;
    this.modeButtons.forEach(function (b) {
      var on = b.dataset.ifcMode === mode;
      b.classList.toggle('is-active', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1;
      if (on && e.main) e.main.setAttribute('aria-labelledby', b.id);
    });
    [].slice.call(root.querySelectorAll('[data-ifc-intro]')).forEach(function (n) { n.hidden = n.dataset.ifcIntro !== mode; });
    if (e.nav) e.nav.hidden = rehab;
    if (e.rehabNav) e.rehabNav.hidden = !rehab;
    if (e.tfilters) e.tfilters.hidden = rehab;
    if (e.assess) e.assess.hidden = !rehab;
    if (e.disclaimer) e.disclaimer.hidden = !rehab;
    if (e.canvas) e.canvas.setAttribute('aria-label', (rehab ? rl.canvasLabel : tr.canvasLabel) || '');
    var ben = (rehab ? rl.benefits : tr.benefits) || {};
    [].slice.call(root.querySelectorAll('[data-ifc-benefit]')).forEach(function (n) { if (ben[n.dataset.ifcBenefit]) n.textContent = ben[n.dataset.ifcBenefit]; });
    if (e.hover) e.hover.hidden = true;
    this.renderHeader();
    this.renderRehabState();               /* also (re)renders products in rehab mode and fixes visibility of summary / filters */
    if (!rehab) { if (e.summary) e.summary.hidden = true; if (e.products) e.products.hidden = false; if (e.countBox) e.countBox.hidden = false; this.renderProducts(); }
    if (this.viewer) { this.viewer.setMode(mode); if (rehab) this.viewer.setRegion(this.rehab.region); else this.viewer.setSelected(this.muscle); }
    if (changed && !o.silent) {
      this.track('finder_mode_selected', { mode: mode });
      if (e.live && o.announce) e.live.textContent = (rehab ? rl.selectedArea : tr.selectedGroup) + ': ' + (rehab ? ((rl.regions || {})[this.rehab.region] || {}).name : ((this.i18n.muscles || {})[this.muscle] || {}).name);
    }
  };

  /* ---------------- 3D viewer (lazy) ---------------- */
  Finder.prototype.observeViewer = function () {
    var self = this, v = this.els.viewer;
    if (!v || !this.els.canvas) return;
    if (!this.root.dataset.modelUrl) return;              /* status text from Liquid stays */
    if (!('IntersectionObserver' in window)) { this.loadViewer(); return; }
    this.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        self.inView = e.isIntersecting;
        if (e.isIntersecting && !self.viewer && !self.viewerLoading) self.loadViewer();
        self.syncActive();
      });
    }, { rootMargin: '300px 0px' });
    this.io.observe(v);
  };

  Finder.prototype.syncActive = function () {
    if (this.viewer) this.viewer.setActive(this.inView && !document.hidden);
  };

  Finder.prototype.showViewerError = function (detail) {
    var s = this.els.status;
    if (!s) return;
    s.hidden = false;
    s.textContent = '';
    var icon = document.createElement('span'); icon.className = 'ifc-mf__status-icon'; icon.setAttribute('aria-hidden', 'true'); icon.textContent = '!';
    var msg = document.createElement('strong'); msg.textContent = (this.i18n.viewer || {}).error || '';
    s.appendChild(icon); s.appendChild(msg);
    if (detail && /[?&]mf_debug/.test(location.search)) { var d = document.createElement('code'); d.textContent = String(detail); d.style.cssText = 'display:block;margin-top:8px;font-size:11px;opacity:.8;word-break:break-word'; s.appendChild(d); }
  };

  /* classic script (not import()) so it works regardless of module/importmap tooling on the theme */
  var scriptPromises = {};
  function loadScript(src) {
    if (window.IFCMuscleViewer) return Promise.resolve();
    if (scriptPromises[src]) return scriptPromises[src];
    return (scriptPromises[src] = new Promise(function (resolve, reject) {
      var el = document.createElement('script');
      el.src = src; el.async = true;
      el.onload = function () { resolve(); };
      el.onerror = function () { delete scriptPromises[src]; reject(new Error('viewer script failed to load: ' + src)); };
      document.head.appendChild(el);
    }));
  }

  Finder.prototype.loadViewer = function () {
    var self = this, d = this.root.dataset;
    if (this.viewerLoading || this.viewer || !d.viewerSrc) return;
    this.viewerLoading = true;
    var style = getComputedStyle(this.root);
    loadScript(d.viewerSrc).then(function () {
      var mod = window.IFCMuscleViewer;
      if (!mod || !mod.createViewer) throw new Error('viewer bundle missing');
      if (self.destroyed) return null;
      return mod.createViewer({
        canvas: self.els.canvas, host: self.els.viewer, modelUrl: d.modelUrl,
        gold: style.getPropertyValue('--ifc-gold').trim() || '#B3933C',
        modelColor: style.getPropertyValue('--ifc-model').trim() || '#55585A',
        reducedMotion: reduced, selected: self.muscle, mode: self.mode, region: self.rehab.region,
        getInsets: function () {
          var v = self.els.viewer.getBoundingClientRect(), tools = self.root.querySelector('.ifc-mf__tools'), label = self.root.querySelector('.ifc-mf__view-label');
          var bottom = tools ? Math.max(0, v.bottom - tools.getBoundingClientRect().top) + 10 : 0;
          var top = label && getComputedStyle(label).display !== 'none' ? 46 : 10;
          return { top: top, bottom: bottom };
        },
        onSelect: function (id) { self.select(id, { announce: true, fromViewer: true }); },
        onHover: function (id, x, y) { self.hover(id, x, y); },
        onRegionSelect: function (id) { self.selectRegion(id, { announce: true, fromViewer: true }); },
        onRegionHover: function (id, x, y) { self.hover(id, x, y, 'region'); }
      });
    }).then(function (viewer) {
      if (!viewer) return;
      if (self.destroyed) { viewer.destroy(); return; }
      self.viewer = viewer;
      if (self.els.status) self.els.status.hidden = true;
      viewer.setSelected(self.muscle);
      if (self.mode === 'rehab') { viewer.setMode('rehab'); viewer.setRegion(self.rehab.region); }
      self.syncActive();
      self.root.dataset.viewerReady = 'true';
    }).catch(function (err) {
      if (self.destroyed || (err && err.message === 'destroyed')) return;
      if (window.console) console.warn('[iFitCore Muscle Finder] 3D viewer unavailable:', err);
      self.showViewerError(err && err.message ? err.message : err);
    }).then(function () { self.viewerLoading = false; });
  };

  /* ---------------- AJAX cart ---------------- */
  Finder.prototype.later = function (fn, ms) { var t = setTimeout(fn, ms); this.timers.push(t); };
  Finder.prototype.addToCart = function (button) {
    var self = this, id = Number(button.dataset.variantId), a = this.i18n.actions || {};
    if (!id || button.disabled || button.classList.contains('is-busy')) return;
    var label = button.querySelector('[data-ifc-add-label]');
    var original = button.dataset.label;
    button.classList.add('is-busy'); button.disabled = true;
    label.textContent = a.adding;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || this.root.dataset.root || '/';
    var sections = [].slice.call(document.querySelectorAll('cart-items-component')).map(function (n) { return n.dataset.sectionId; }).filter(Boolean);
    var body = { items: [{ id: id, quantity: 1 }] };
    if (sections.length) body.sections = sections.join(',');

    fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(function (res) { return res.json().then(function (data) { if (!res.ok || data.status) { var e = new Error(data.description || data.message || 'cart'); e.cart = true; throw e; } return data; }); })
      .then(function (data) {
        self.track('finder_add_to_cart', { productId: button.dataset.productId, variantId: String(id), mode: self.mode });
        button.classList.remove('is-busy'); button.classList.add('is-done');
        label.textContent = a.added;
        if (self.els.live) self.els.live.textContent = a.added;
        /* same payload as the Horizon theme's CartAddEvent, so cart bubble / drawer update without extra code */
        var ev = new Event('cart:update', { bubbles: true });
        ev.detail = { resource: {}, sourceId: String(id), data: { source: 'product-form-component', itemCount: 1, productId: button.dataset.productId, variantId: String(id), sections: data.sections } };
        button.dispatchEvent(ev);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'ifitcore-muscle-finder' } }));
        self.later(function () { button.classList.remove('is-done'); label.textContent = original; button.disabled = false; }, 1800);
      })
      .catch(function (err) {
        if (window.console) console.warn('[iFitCore Muscle Finder] cart:', err);
        button.classList.remove('is-busy'); button.classList.add('is-error');
        label.textContent = a.tryAgain;
        if (self.els.live) self.els.live.textContent = (err && err.cart && err.message !== 'cart' ? err.message : a.error);
        self.later(function () { button.classList.remove('is-error'); label.textContent = original; button.disabled = false; }, 2200);
      });
  };

  Finder.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    this.ac.abort();
    if (this.io) this.io.disconnect();
    this.timers.forEach(clearTimeout);
    if (this.viewer) { this.viewer.destroy(); this.viewer = null; }
  };

  /* ---------------- bootstrap / Theme Editor ---------------- */
  function scan(scope) {
    [].slice.call((scope || document).querySelectorAll('[data-ifc-mf]')).forEach(function (root) {
      if (instances.has(root)) return;
      instances.set(root, new Finder(root));
    });
  }
  function destroyIn(scope) {
    instances.forEach(function (inst, root) {
      if (scope.contains(root)) { inst.destroy(); instances.delete(root); }
    });
  }
  /* sections replaced by the Theme Editor leave detached roots behind: clean them up */
  function prune() {
    instances.forEach(function (inst, root) { if (!root.isConnected) { inst.destroy(); instances.delete(root); } });
  }
  document.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });
  document.addEventListener('shopify:section:load', function (e) { prune(); scan(e.target); });

  window.IFCMuscleFinder = { scan: function () { prune(); scan(); }, instances: instances, version: '2.0.0', config: { rehab: REHAB, regionTags: REGION_TAGS, rehabFilters: REHAB_FILTERS } };
  scan();
})();